import React, { createContext, useContext, useState, useRef, useCallback, useMemo } from 'react';
import {
  Phase, ContextConfig, ContextKey, StateId, StateEntry,
  DetectedScene, SensorValues, Baselines, GazeData,
} from '../types';
import { CONTEXTS } from '../config/contexts';
import { BaselineStore } from '../engine/BaselineStore';

interface ProctorState {
  phase: Phase;
  setPhase: (p: Phase) => void;
  contextKey: ContextKey;
  setContextKey: (k: ContextKey) => void;
  config: ContextConfig;
  calibStep: number;
  setCalibStep: (s: number) => void;
  riskLevel: number;
  setRiskLevel: (r: number) => void;
  currentState: StateId;
  setCurrentState: (s: StateId) => void;
  stateHistory: StateEntry[];
  pushStateHistory: (entry: StateEntry) => void;
  detectedScenes: DetectedScene[];
  addDetectedScene: (scene: DetectedScene) => void;
  sensorValues: SensorValues;
  setSensorValues: (v: SensorValues) => void;
  baselines: Baselines;
  baselineStore: React.MutableRefObject<BaselineStore>;
  refreshBaselines: () => void;
  gazeData: GazeData | null;
  setGazeData: (g: GazeData | null) => void;
  startTime: number;
  setStartTime: (t: number) => void;
  deviceIsFlat: boolean;
  setDeviceIsFlat: (f: boolean) => void;
}

const defaultSensorValues: SensorValues = {
  gaze:     { value: '--', zScore: 0, active: false },
  keyboard: { value: '--', zScore: 0, active: false },
  voice:    { value: '--', zScore: 0, active: false },
  stylus:   { value: '--', zScore: 0, active: false },
  body:     { value: '--', zScore: 0, active: false },
  focus:    { value: '--', zScore: 0, active: false },
};

const ProctorContext = createContext<ProctorState | null>(null);

export function ProctorProvider({ children }: { children: React.ReactNode }) {
  const [phase, setPhase] = useState<Phase>('ready');
  const [contextKey, setContextKey] = useState<ContextKey>('EXAM');
  const [calibStep, setCalibStep] = useState(0);
  const [riskLevel, setRiskLevel] = useState(0);
  const [currentState, setCurrentState] = useState<StateId>('FOCUSED');
  const [stateHistory, setStateHistory] = useState<StateEntry[]>([]);
  const [detectedScenes, setDetectedScenes] = useState<DetectedScene[]>([]);
  const [sensorValues, setSensorValues] = useState<SensorValues>(defaultSensorValues);
  const [baselines, setBaselines] = useState<Baselines>({
    yaw: null, pitch: null, keyInterval: null, voiceRms: null,
    stylusPressure: null, stylusSpeed: null, shoulderAngle: null,
  });
  const [gazeData, setGazeData] = useState<GazeData | null>(null);
  const [startTime, setStartTime] = useState(0);
  const [deviceIsFlat, setDeviceIsFlat] = useState(false);

  const baselineStore = useRef(new BaselineStore());

  const config = useMemo(() => CONTEXTS[contextKey], [contextKey]);

  const pushStateHistory = useCallback((entry: StateEntry) => {
    setStateHistory(prev => {
      const next = [...prev, entry];
      if (next.length > 40) next.shift();
      return next;
    });
  }, []);

  const addDetectedScene = useCallback((scene: DetectedScene) => {
    setDetectedScenes(prev => {
      const next = [scene, ...prev];
      if (next.length > 30) next.pop();
      return next;
    });
  }, []);

  const refreshBaselines = useCallback(() => {
    setBaselines({ ...baselineStore.current.baselines });
  }, []);

  const value = useMemo<ProctorState>(() => ({
    phase, setPhase,
    contextKey, setContextKey,
    config,
    calibStep, setCalibStep,
    riskLevel, setRiskLevel,
    currentState, setCurrentState,
    stateHistory, pushStateHistory,
    detectedScenes, addDetectedScene,
    sensorValues, setSensorValues,
    baselines, baselineStore, refreshBaselines,
    gazeData, setGazeData,
    startTime, setStartTime,
    deviceIsFlat, setDeviceIsFlat,
  }), [
    phase, contextKey, config, calibStep, riskLevel, currentState,
    stateHistory, pushStateHistory, detectedScenes, addDetectedScene,
    sensorValues, baselines, refreshBaselines, gazeData, startTime, deviceIsFlat,
  ]);

  return (
    <ProctorContext.Provider value={value}>
      {children}
    </ProctorContext.Provider>
  );
}

export function useProctor(): ProctorState {
  const ctx = useContext(ProctorContext);
  if (!ctx) throw new Error('useProctor must be inside ProctorProvider');
  return ctx;
}
